import * as React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, RefreshCw, Loader2, AlertTriangle, Scale, Receipt } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { usePaymentDetails, useRefundPayment, useVerifyOnHoldPayment } from '@/lib/api/payments';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useToast } from '@/components/ui/use-toast';
import { DateDisplay } from '@/components/date-display';
import { useFormatDate } from '@/hooks/use-format-date';

import { useDownloadRefundReceipt } from './hooks/use-download-refund-receipt';
import PaymentDetailsSkeleton from './components/skeleton';
import { PaymentOverviewCard } from './components/payment-overview-card';
import { PaymentCustomerCard } from './components/payment-customer-card';
import { PaymentTransactionsCard } from './components/payment-transactions-card';
import { PaymentDetailsCard } from './components/payment-details-card';
import { Payment3DSCard } from './components/payment-3ds-card';
import { PaymentRefundDialog } from './components/payment-refund-dialog';
import { BackButton } from '@/components/ui/BackButton';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(PaymentDetailsPage, PERMISSIONS.PAYMENTS_VIEW);

function PaymentDetailsPage() {
  const { t } = useTranslation('payments');
  const { id } = useParams();
  const { toast } = useToast();
  const { data: payment, isLoading, error } = usePaymentDetails(id!);
  const { orgRoutes } = useOrgNavigate();
  const refundMutation = useRefundPayment();
  const verifyOnHoldMutation = useVerifyOnHoldPayment();
  const refundReceipt = useDownloadRefundReceipt(payment);
  const { format: formatTz, isReady: isTzReady } = useFormatDate();

  const [isRefundDialogOpen, setIsRefundDialogOpen] = React.useState(false);
  const [refundRequestId, setRefundRequestId] = React.useState<string | null>(null);

  // Function to check if payment can be refunded
  const canRefund = React.useCallback(() => {
    if (!payment) return false;
    const totalAmount = payment.amount || 0;
    const refundedAmount = payment.refundedAmount || 0;
    return totalAmount > refundedAmount && (payment.status === 'succeeded' || (payment.status === 'pending' && payment.subStatus === 'authorized'));
  }, [payment]);

  // Handle refund action
  const handleRefund = React.useCallback(() => {
    if (!canRefund()) {
      toast({
        title: t('page.cannotRefundTitle'),
        description: t('page.cannotRefundDescription'),
        variant: 'destructive',
      });
      return;
    }
    setRefundRequestId(crypto.randomUUID());
    setIsRefundDialogOpen(true);
  }, [canRefund, toast, t]);

  // Handle refund confirmation
  const handleRefundConfirm = React.useCallback(
    (params: { refundType: 'full' | 'partial'; amount?: number; cancelSubscription: boolean }) => {
      if (!id || refundRequestId === null) return;

      refundMutation.mutate(
        {
          paymentIds: [id],
          requestId: refundRequestId,
          amount: params.refundType === 'partial' ? params.amount : undefined,
          cancelSubscription: params.cancelSubscription,
        },
        {
          onSuccess: () => {
            setIsRefundDialogOpen(false);
          },
          // Without this a failed refund was silent on this page: the spinner stopped, the
          // dialog stayed open and nothing was shown, so the operator clicked Refund again.
          // `error.message` carries the per-transaction processor reason the tRPC router
          // appends (#TAG-2730).
          onError: (error) => {
            toast({
              title: t('refundFailed'),
              description: error.message || t('refundFailedDescription'),
              variant: 'destructive',
            });
            // Already-refunded is deterministic and permanent (#TAG-2730): a retry can only
            // fail the same way, so close the dialog instead of leaving it open to be retried.
            // Matched by message text since the router collapses every refund failure into a
            // generic INTERNAL_SERVER_ERROR (payments.ts:123-127) with no distinct error code.
            // A disputed payment is refused before any processor call (#TAG-2899): same reasoning.
            if (
              error.message?.includes('Already refunded at the processor') ||
              error.message?.includes('a disputed payment cannot be refunded')
            ) {
              setIsRefundDialogOpen(false);
            }
          },
        },
      );
    },
    [id, refundMutation, refundRequestId, toast, t],
  );

  // Handle export
  const handleExport = React.useCallback(() => {
    if (!payment) return;

    // Create CSV content
    const csvData = [
      ['Payment ID', 'Status', 'Amount', 'Currency', 'Customer', 'Created At'],
      [
        payment.id,
        payment.status,
        payment.amount || 0,
        payment.currency || 'USD',
        `${payment.customer?.firstName || ''} ${payment.customer?.lastName || ''}`.trim(),
        payment.createdAt ? formatTz(payment.createdAt, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
      ],
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `payment-${payment.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: t('page.exportSuccessTitle'),
      description: t('page.exportSuccessDescription'),
    });
  }, [payment, toast, formatTz, t]);

  if (isLoading) {
    return <PaymentDetailsSkeleton />;
  }

  if (error) {
    const parsed = parseTRPCError(error);
    if (parsed.code === 'NOT_FOUND') {
      return <Navigate to={orgRoutes.PAYMENTS()} replace state={{ paymentNotFound: id }} />;
    }
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <ErrorDisplay error={parsed} />
      </div>
    );
  }

  if (!payment) {
    return <Navigate to={orgRoutes.PAYMENTS()} replace state={{ paymentNotFound: id }} />;
  }

  const hasTransactions = payment.transactions && payment.transactions.length > 0;
  const showRefundButton =
    (payment.status === 'succeeded' || (payment.status === 'pending' && payment.subStatus === 'authorized')) && (payment.refundedAmount || 0) < (payment.amount || 0);
  // A chargeback blocks the refund: the processor declines it and the back end refuses it
  // first. An inquiry stays refundable, that is how it gets closed. Mirrors
  // `isRefundBlockedByDispute` in src/app/services/payment.ts (#TAG-2899).
  const refundBlockedByDispute = Boolean(payment.disputeCaseId) && payment.disputeType !== 'information-request';

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <BackButton to={orgRoutes.PAYMENTS()} />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{t('paymentDetails')}</h1>
                <PaymentStatusBadge status={payment.status as any} subStatus={payment.subStatus} />
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                <DateDisplay value={payment.createdAt} />
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <CopyableResourceBadge name={t('paymentId')} value={payment.id} variant="outline" />
            <div className="flex gap-2">
              {payment.status === 'on_hold' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    if (!id) return;
                    verifyOnHoldMutation.mutate(
                      { paymentId: id },
                      {
                        onSuccess: () => {
                          toast({ title: t('page.paymentReceivedTitle'), description: t('page.paymentReceivedDescription') });
                        },
                        onError: (e) => {
                          toast({
                            title: t('page.verifyPaymentFailedTitle'),
                            description: e.message || t('page.verifyPaymentFailedDescription'),
                            variant: 'destructive',
                          });
                        },
                      },
                    );
                  }}
                  disabled={verifyOnHoldMutation.isPending}
                >
                  {verifyOnHoldMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {t('page.confirmPaymentReceived')}
                </Button>
              )}
              {showRefundButton && (
                // A disabled button gets no pointer events, so the reason sits on its wrapper.
                <span title={refundBlockedByDispute ? t('page.dispute.refundDisabled') : undefined}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefund}
                    disabled={refundMutation.isPending || refundBlockedByDispute}
                  >
                    {refundMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    {t('refund')}
                  </Button>
                </span>
              )}
              {refundReceipt.hasRefund && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void refundReceipt.download();
                  }}
                  disabled={refundReceipt.isGenerating}
                >
                  {refundReceipt.isGenerating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Receipt className="mr-2 h-4 w-4" />
                  )}
                  {t('refundReceipt.buttonLabel')}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleExport} disabled={!isTzReady}>
                <Download className="mr-2 h-4 w-4" />
                {t('export')}
              </Button>
            </div>
          </div>
        </div>

        {/* Test Payment Warning */}
        {payment.draft && (
          <Alert variant="warning" className="mt-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('page.testPayment.title')}</AlertTitle>
            <AlertDescription>{t('page.testPayment.description')}</AlertDescription>
          </Alert>
        )}

        {/* Dispute Alert */}
        {payment.isDisputed && (
          <Alert variant="destructive">
            <Scale className="h-4 w-4" />
            <AlertTitle className="flex items-center gap-2">
              {t('page.dispute.title')}
              {payment.disputeType && (
                <Badge variant="destructive" className="text-xs">{payment.disputeType}</Badge>
              )}
              {payment.disputeStatus && (
                <Badge variant="outline" className="text-xs">{payment.disputeStatus}</Badge>
              )}
            </AlertTitle>
            <AlertDescription className="space-y-1">
              {payment.disputeReasonCode && (
                <span>{t('page.dispute.reasonCode')} <strong>{payment.disputeReasonCode}</strong></span>
              )}
              {payment.disputeCaseId && (
                <span className="ml-3">{t('page.dispute.case')} <strong>{payment.disputeCaseId}</strong></span>
              )}
              {payment.disputedAt && (
                <span className="ml-3">{t('page.dispute.filed')} <DateDisplay value={payment.disputedAt} /></span>
              )}
              {payment.disputeNote && (
                <p className="mt-1 text-sm">{payment.disputeNote}</p>
              )}
              {refundBlockedByDispute && <p className="mt-1 text-sm">{t('page.dispute.refundDisabled')}</p>}
              {payment.disputeCaseId && (
                <p className="mt-1">
                  <Link to={orgRoutes.DISPUTE_DETAILS(payment.disputeCaseId)} className="font-medium underline">
                    {t('page.dispute.view')}
                  </Link>
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            <PaymentOverviewCard payment={payment} />
            <Payment3DSCard payment={payment} />
            <PaymentDetailsCard payment={payment} />
            {hasTransactions && <PaymentTransactionsCard payment={payment} />}
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            <PaymentCustomerCard payment={payment} />
            {/* TODO: Add PaymentTimelineCard when available */}
          </div>
        </div>
      </div>

      {/* Refund Dialog */}
      <PaymentRefundDialog
        payment={payment}
        open={isRefundDialogOpen}
        onOpenChange={setIsRefundDialogOpen}
        onConfirm={handleRefundConfirm}
        isProcessing={refundMutation.isPending}
      />
    </div>
  );
}
